import React, { FC, useState } from 'react';
import { Box } from '@mui/material';
import {
  ICandidateSourcingFilters,
  FilterKey,
  filterToLabel,
  ICandidateSourcingSearch
} from '../types';
import StyledModal from '../../Components/GenericModal/StyledModal';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../../Components/GenericModal/ModalFooterButtons';
import FilterSection from './FilterSection';
import { yearsOfExperienceOptions } from './filterConfig';
import { useFilterState } from './useFilterState';
import GenericDialog from '../../Components/Modals/GenericDialog';

interface EditFiltersModalProps {
  filterOptions?: ICandidateSourcingSearch['filter_options'];
  filters: ICandidateSourcingFilters;
  isOpen: boolean;
  onClose: () => void;
  onFiltersChange: (filters: ICandidateSourcingFilters) => void;
}

export const EditFiltersModal: FC<EditFiltersModalProps> = ({
  filterOptions = {},
  filters,
  isOpen,
  onClose,
  onFiltersChange
}) => {
  const [confirmCloseIsOpen, setConfirmCloseIsOpen] = useState(false);

  const {
    localFilters,
    newValues,
    hasChanges,
    handleAddFilter,
    handleRemoveFilter,
    handleValueChange,
    resetState
  } = useFilterState({ filters, isOpen });

  const handleSave = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleCancel = () => {
    if (hasChanges) {
      setConfirmCloseIsOpen(true);
    } else {
      resetState();
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setConfirmCloseIsOpen(false);
    resetState();
    onClose();
  };

  const getFilterOptions = (key: FilterKey): string[] => {
    if (key === 'years_of_experience') {
      return yearsOfExperienceOptions;
    }
    return filterOptions[key as keyof typeof filterOptions] || [];
  };

  const renderFilterSection = (key: FilterKey) => {
    const values = localFilters[key];
    const newValue = newValues[key] || '';
    const options = getFilterOptions(key);

    return (
      <FilterSection
        key={key}
        filterKey={key}
        values={values}
        newValue={newValue}
        options={options}
        onValueChange={(value) => handleValueChange(key, value)}
        onAdd={(value) => handleAddFilter(key, value)}
        onRemove={handleRemoveFilter}
      />
    );
  };

  return (
    <StyledModal
      isOpen={isOpen}
      handleClose={handleCancel}
      styleOverrides={{
        maxWidth: '700px',
        maxHeight: '80vh'
      }}
      label="edit-filters-modal"
      modalTitle="Edit Filters"
      subHeaderStyling={false}
    >
      <Box sx={{ ...sharedClasses.mainModalContent, height: '100%' }}>
        <Box sx={{ mb: 3 }}>
          {Object.keys(filterToLabel).map((key) => renderFilterSection(key as FilterKey))}
        </Box>
      </Box>
      <ModalFooterButtons
        primaryButtonText="Save Changes"
        primaryButtonCallback={handleSave}
        primaryButtonDisabled={!hasChanges}
        secondaryButtonText="Cancel"
        secondaryButtonCallback={handleCancel}
        primaryButtonID="save-filters-button"
        secondaryButtonID="cancel-filters"
      />
      <GenericDialog
        isDialogOpen={confirmCloseIsOpen}
        setDialogOpen={setConfirmCloseIsOpen}
        title="Close Edit Filters?"
        description="All unsaved changes will be lost."
        buttonCallback={() => handleConfirmClose()}
        callbackLoading={false}
        buttonText="Close Edit Filters"
        url=""
        secondaryButtonText="Keep Editing"
      />
    </StyledModal>
  );
};

export default EditFiltersModal;
